import React from 'react'

type UseCustomFieldValueOptions = {
  meta: APIMeta[]
  setMeta?: React.Dispatch<React.SetStateAction<APIMeta[]>>
  updateMetaVal?: (
    key: string,
    value: string,
    row?: number,
    rowKey?: string
  ) => void
  onValueChange?: () => void
}

const useCustomFieldValues = ({
  meta,
  setMeta: setMetaProp,
  updateMetaVal: updateMetaValProp,
  onValueChange: onValueChangeProp
}: UseCustomFieldValueOptions) => {
  const updateMetaValRef = React.useRef(updateMetaValProp)
  const setMetaRef = React.useRef(setMetaProp)
  const onValueChangeRef = React.useRef(onValueChangeProp)

  React.useEffect(() => {
    updateMetaValRef.current = updateMetaValProp
  }, [updateMetaValProp])

  React.useEffect(() => {
    setMetaRef.current = setMetaProp
  }, [setMetaProp])

  React.useEffect(() => {
    onValueChangeRef.current = onValueChangeProp
  }, [onValueChangeProp])

  // Adds or updates meta value by id
  const updateMetaVal = React.useCallback(
    (key: string, value: string, row: number, row_key: string) => {
      if (updateMetaValRef.current) {
        updateMetaValRef.current(key, value, row, row_key)
        return
      }

      if (!setMetaRef.current) {
        return
      }

      setMetaRef.current(old => {
        const changed = [...old]
        let idx: number

        if (row_key) {
          idx = changed.findIndex(
            obj => obj.key === key && obj.row === row && obj.row_key === row_key
          )
        } else {
          idx = changed.findIndex(obj => obj.key === key)
        }

        if (idx >= 0) {
          changed[idx].value = value
        } else {
          changed.push({ key, value, row, row_key })
        }

        return changed
      })
    },
    []
  )

  const handleValueChange = React.useCallback(
    (
      e: any,
      field: LocalCustomField,
      row?: number,
      rowKey?: string,
      parent?: LocalCustomField
    ) => {
      const { field_type } = field
      const id = parent?.id || field.id
      const val = field_type === 'boolean' ? e.target.checked : e.target.value

      if (onValueChangeRef.current) {
        onValueChangeRef.current()
      }

      // Store boolean as string representation
      if (field_type === 'boolean') {
        updateMetaVal(id, `${val}`, row, rowKey)
        return
      }

      const basicFieldTypes: LocalCustomField['field_type'][] = [
        'text',
        'multiline',
        'dropdown',
        'date-only',
        'date-time',
        'month-year',
        'user',
        'phone'
      ]

      // Store pre-formatted text
      if (basicFieldTypes.includes(field_type)) {
        updateMetaVal(id, val, row, rowKey)
        return
      }

      // Logic to add or remove selections
      if (field_type === 'multiselect') {
        // Split selections, remove empty strings
        let prev
        if (rowKey) {
          prev = (
            meta.find(
              data =>
                data.key === id && data.row_key === rowKey && data.row === row
            )?.value ?? ''
          )
            .split('|')
            .filter(Boolean)
        } else {
          prev = (meta.find(data => data.key === id)?.value ?? '')
            .split('|')
            .filter(Boolean)
        }

        let newVal = ''
        if (prev.includes(val)) {
          // Remove selected value case
          newVal = prev.filter(x => x !== val).join('|')
        } else {
          // Add selection case
          newVal = [...prev, val].join('|')
        }

        updateMetaVal(id, newVal, row, rowKey)
        return
      }

      console.warn(`Unknown field type ${field_type}`)
    },
    [updateMetaVal, meta]
  )

  return { handleValueChange }
}

export default useCustomFieldValues
